import { useState, useCallback } from 'react';
import eventService from '../services/eventService';
import { getEventYear, getEffectiveEventStatus, getCurrentYear } from '../utils/eventStatus';

export const useEventManagement = () => {
  const [events, setEvents] = useState([]);
  const [filteredEvents, setFilteredEvents] = useState([]);
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [filters, setFilters] = useState({
    title: '',
    status: 'all',
    dateRange: 'all',
    year: getCurrentYear()
  });

  // Function to apply filters
  const applyFilters = useCallback((eventList, currentFilters) => {
    return eventList.filter(event => {
      // Filtro por título
      if (currentFilters.title && !event.title.toLowerCase().includes(currentFilters.title.toLowerCase())) {
        return false;
      }
      // Filtro por estado (derivado de la fecha, no del campo guardado)
      if (currentFilters.status !== 'all' && getEffectiveEventStatus(event) !== currentFilters.status) {
        return false;
      }
      // Filtro por año
      if (currentFilters.year && currentFilters.year !== 'all') {
        if (String(getEventYear(event)) !== String(currentFilters.year)) {
          return false;
        }
      }
      // Filtro por rango de fechas
      if (currentFilters.dateRange !== 'all') {
        const eventDate = new Date(event.eventDate);
        const today = new Date();
        today.setHours(0, 0, 0, 0);

        switch (currentFilters.dateRange) {
          case 'today':
            const tomorrow = new Date(today);
            tomorrow.setDate(tomorrow.getDate() + 1);
            if (eventDate < today || eventDate >= tomorrow) return false;
            break;
          case 'week':
            const weekFromNow = new Date(today);
            weekFromNow.setDate(weekFromNow.getDate() + 7);
            if (eventDate < today || eventDate >= weekFromNow) return false;
            break;
          case 'month':
            const monthFromNow = new Date(today);
            monthFromNow.setMonth(monthFromNow.getMonth() + 1);
            if (eventDate < today || eventDate >= monthFromNow) return false;
            break;
          case 'past':
            if (eventDate >= today) return false;
            break;
          case 'upcoming':
            if (eventDate < today) return false;
            break;
          default:
            // Para 'all' o cualquier otro valor, no filtrar por fecha
            break;
        }
      }
      return true;
    });
  }, []);

  const fetchEvents = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await eventService.getEvents();
      setEvents(data);
      setFilteredEvents(applyFilters(data, filters));
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [filters, applyFilters]);

  const fetchActiveEvents = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await eventService.getActiveEvents();
      setEvents(data);
      setFilteredEvents(applyFilters(data, filters));
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [filters, applyFilters]);

  const searchEventsByTitle = useCallback(async (title) => {
    setLoading(true);
    setError(null);
    try {
      const data = await eventService.searchEventsByTitle(title);
      setEvents(data);
      setFilteredEvents(data);
      return data;
    } catch (err) {
      setError(err.message);
      setEvents([]);
      setFilteredEvents([]);
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  // Aplicar filtros (solo actualiza estado, no aplica)
  const applyEventFilters = useCallback((newFilters) => {
    setFilters(newFilters);
    // Solo aplicar filtros si se está aplicando explícitamente
    if (newFilters.applyNow) {
      const { applyNow, ...filtersToApply } = newFilters;
      setFilteredEvents(applyFilters(events, filtersToApply));
    }
  }, [events, applyFilters]);

  // Aplicar filtros explícitamente (cuando se presiona el botón)
  const applyFiltersNow = useCallback(() => {
    setFilteredEvents(applyFilters(events, filters));
  }, [events, filters, applyFilters]);

  const clearFilters = useCallback(() => {
    const defaultFilters = { title: '', status: 'all', dateRange: 'all', year: getCurrentYear() };
    setFilters(defaultFilters);
    setFilteredEvents(applyFilters(events, defaultFilters));
  }, [events, applyFilters]);

  const createEvent = useCallback(async (eventData) => {
    setLoading(true);
    setError(null);
    
    try {
      const newEvent = await eventService.createEvent(eventData);
      const updatedEvents = [...events, newEvent];
      setEvents(updatedEvents);
      setFilteredEvents(applyFilters(updatedEvents, filters));
      return newEvent;
    } catch (err) {
      // NO establecer el error global aquí
      // Solo lanzar la excepción para que el modal la maneje
      throw err;
    } finally {
      setLoading(false);
    }
  }, [events, filters, applyFilters]);

  const updateEvent = useCallback(async (id, eventData) => {
    setLoading(true);
    setError(null);
    
    try {
      const updatedEvent = await eventService.updateEvent(id, eventData);
      const updatedEvents = events.map(event => 
        event.id === id ? updatedEvent : event
      );
      setEvents(updatedEvents);
      setFilteredEvents(applyFilters(updatedEvents, filters));
      
      // Actualizar evento seleccionado si es el mismo
      if (selectedEvent?.id === id) {
        setSelectedEvent(updatedEvent);
      }
      
      return updatedEvent;
    } catch (err) {
      // NO establecer el error global aquí
      // Solo lanzar la excepción para que el modal la maneje
      throw err;
    } finally {
      setLoading(false);
    }
  }, [events, filters, selectedEvent, applyFilters]);

  const deleteEvent = useCallback(async (id) => {
    setLoading(true);
    setError(null);
    
    try {
      await eventService.deleteEvent(id);
      const updatedEvents = events.filter(event => event.id !== id);
      setEvents(updatedEvents);
      setFilteredEvents(applyFilters(updatedEvents, filters));
      
      // Limpiar evento seleccionado si es el mismo
      if (selectedEvent?.id === id) {
        setSelectedEvent(null);
      }
      
      return true;
    } catch (err) {
      setError(err.message);
      throw err;
    } finally {
      setLoading(false);
    }
  }, [events, filters, selectedEvent, applyFilters]);

  const updateEventStatus = useCallback(async (id, status) => {
    setLoading(true);
    setError(null);
    
    try {
      const updatedEvent = await eventService.updateEventStatus(id, status);
      const updatedEvents = events.map(event => 
        event.id === id ? updatedEvent : event
      );
      setEvents(updatedEvents);
      setFilteredEvents(applyFilters(updatedEvents, filters));
      
      // Actualizar evento seleccionado si es el mismo
      if (selectedEvent?.id === id) {
        setSelectedEvent(updatedEvent);
      }
      
      return updatedEvent;
    } catch (err) {
      setError(err.message);
      throw err;
    } finally {
      setLoading(false);
    }
  }, [events, filters, selectedEvent, applyFilters]);

  const clearError = useCallback(() => {
    setError(null);
  }, []);

  const clearSelectedEvent = useCallback(() => {
    setSelectedEvent(null);
  }, []);

  const clearSearch = useCallback(() => {
    fetchEvents();
  }, [fetchEvents]);

  return {
    events: filteredEvents,
    allEvents: events,
    selectedEvent,
    loading,
    error,
    filters,
    fetchEvents,
    fetchActiveEvents,
    searchEventsByTitle,
    applyEventFilters,
    applyFiltersNow,
    clearFilters,
    createEvent,
    updateEvent,
    deleteEvent,
    updateEventStatus,
    clearError,
    clearSelectedEvent,
    clearSearch,
    setSelectedEvent,
    setEvents
  };
}; 